(ns minorodata.components.sql-console.core
  (:require [reagent.core :as reagent]
            [re-posh.core :as re-posh]
            [applied-science.js-interop :as js-interop]
            [clojure.string]
            [minorodata.app.handlers :refer [dispatch dispatch-sync]]
            [minorodata.components.connections.forms.generator :as form-generator]
            [minorodata.components.spinner.core :as c.spinner]
            [minorodata.sql-console.model :refer [->sql-editor]]
            [ui.drawer :refer [drawer]]
            [ui.button :refer [button]]
            [ui.fields :refer [input]]
            [ui.dialog :refer [dialog dialog-actions]]
            [ui.typography :as typography]
            [ui.tabs]
            [lucide-react :as lucide :refer [Database]]))


(defonce editor-ref
  (atom nil))


(defn error-message-preview []
  (let [loading?      @(re-posh/subscribe [:mold-http/loading? :sql-console.api/post-query])

        tab      (or @(re-posh/subscribe [:sql-editor/prop :sql-editor/current-tab]) 0)
        tdc      @(re-posh/subscribe [:sql-editor/prop :sql-editor/tab-data-container])
        tab-data (nth tdc tab {})

        error-message (:error-message tab-data)]

    [:div {:class "p-4"}
     [:div {:class ""}
      [typography/h3 {:class "pb-4"}
       "Failed to execute SQL statement:"]
      (when loading?
        [c.spinner/inline-configurable {:color "primary" :size 20}])]

     [:div {:class ""}
      [:pre {:class "text-kleene-error-01 border rounded-xs border-kleene-error-01 p-2"}
       (str error-message)]]]))


(def DEFAULT_MAX_ROWS 2000)

(defn execute-command []
  (let [max-rows         (or @(re-posh/subscribe [:sql-editor/prop :sql-editor/max-rows-count])
                             DEFAULT_MAX_ROWS)
        selection        @(re-posh/subscribe [:sql-editor/prop :sql-editor/selection])
        blank-selection? (clojure.string/blank? selection)
        tab              (or @(re-posh/subscribe [:sql-editor/prop :sql-editor/current-tab]) 0)
        tdc              @(re-posh/subscribe [:sql-editor/prop :sql-editor/tab-data-container])
        tab-data         (nth tdc tab {})
        metadata         (:metadata tab-data)
        code             (:code tab-data)
        {:keys [environment-id]} metadata]

    (if blank-selection?
      (dispatch [:sql-console.api/post-query {:query code :max-rows max-rows :environment-id environment-id}])

      (let [model         (js-interop/call @editor-ref :getModel)
            selection     (js-interop/call @editor-ref :getSelection)
            selected-text (js-interop/call model :getValueInRange selection)]
        (dispatch [:sql-console.api/post-query {:query selected-text
                                                :max-rows max-rows
                                                :environment-id environment-id}])))))


(defn custom-actions [{:keys [monaco]}]
  [{:id          "execute-query"
    :label       "Run Code"
    :contextMenuOrder 0
    :contextMenuGroupId "2_execution"
    :keybindings [(bit-or (js-interop/get-in monaco [:KeyMod :CtrlCmd])
                          (js-interop/get-in monaco [:KeyCode :Enter]))]
    :run         execute-command}])


(defn close-create-transform-drawer []
  (dispatch [:sql-editor/update {:sql-editor/create-transform-drawer-open? false}]))

(defn open-create-transform-drawer []
  (dispatch [:sql-editor/update {:sql-editor/create-transform-drawer-open? true}]))


(defn close-create-data-view-modal []
  (dispatch [:sql-editor/update {:sql-editor/create-data-view-modal-open? false
                                 :sql-editor/data-view-name ""}]))

(defn open-create-data-view-modal []
  (dispatch [:sql-editor/update {:sql-editor/create-data-view-modal-open? true}]))

(defn close-sql-console-modal []
  (dispatch [:sql-console-modal.events/toggle-modal false]))


(defn update-max-rows-option [max-rows]
  (dispatch [:sql-editor/update {:sql-editor/max-rows-count (js/parseInt max-rows 10)}]))

(defn init-sql-editor-state []
  (let [query          @(re-posh/subscribe [:app-route/prop :app-route/query])
        codes          @(re-posh/subscribe [:sql-console.misc/load-initial-tabs])
        metadata       @(re-posh/subscribe [:sql-console.misc/load-initial-metadata])
        config         @(re-posh/subscribe [:sql-console.misc/load-initial-config])

        {:keys [sidebar-width data-preview-height editor-height sidebar-tab]
         :or   {sidebar-width 350 editor-height 500}}
        (js->clj config :keywordize-keys true)

        empty-tab-data {:code                ""
                        :results-tab         0
                        :results-tab-configs {}
                        :metadata            {}}

        existing-tabs  @(re-posh/subscribe [:sql-editor/prop :sql-editor/tab-data-container])]

    ;; When user is returning to SQL console and didn't refresh the page
    (when (nil? existing-tabs)
      (let [{:keys [code new-tab]} query
            code-from-query? (and code new-tab)
            tab-code         (if code-from-query?
                               code
                               (first codes))
            codes            (if code-from-query?
                               (conj (vec codes) code)
                               codes)]

        (dispatch-sync
         [:sql-editor/add
          (->sql-editor
           (assoc empty-tab-data
                  :sidebar-tab sidebar-tab
                  :sidebar-width sidebar-width
                  :data-preview-height data-preview-height
                  :editor-height editor-height
                  :code tab-code
                  :metadata (if code-from-query?
                              {}
                              (or (first metadata) {}))
                  :tab-data-container (vec (map-indexed (fn generate-empty-tab-data [idx code]
                                                          (assoc empty-tab-data
                                                                 :code code
                                                                 :metadata (nth metadata idx {})))
                                                        codes))))])
        (dispatch-sync
         [:sql-editor/update
          (->sql-editor {:sidebar-tab         sidebar-tab
                         :sidebar-width       sidebar-width
                         :data-preview-height data-preview-height
                         :editor-height       editor-height
                         :tab-count           (count codes)
                         :current-tab         (if code-from-query?
                                                (- (count codes) 1)
                                                0)})]))))

  (let [history-tab @(re-posh/subscribe [:sql-editor/prop :sql-editor/history-tab])]
    (when-not (some? history-tab)
      (dispatch [:sql-editor/update {:sql-editor/history-tab :history}])))

  (dispatch [:sql-console.events/reset-favourites])
  (dispatch [:sql-console.events/reset-history])

  (dispatch [:sql-console.api/fetch-metadata {:object "catalogs"}]))


(defn edit-query [{:keys [id on-close]}]
  (let [query @(re-posh/subscribe [:sql-query/pull [:sql-query/sql-query-id id]])
        db-id (:db/id query)]
    [:div {:class ""}
     [typography/h1 {:class "mb-6 pr-8"}
      "Edit query history entry"]

     (form-generator/generate
      {:format-for-inputs identity
       :format-for-api    identity

       :id :sql-query-history-edit-form

       :blocks [{:block-type :input
                 :label      "Name"
                 :required   true
                 :name       :name
                 :value      ""}
                {:block-type :form-value
                 :name       :sql-query-id
                 :value      id}
                {:block-type :submit
                 :content    "Save"}]}
      {:on-close       on-close
       :initial-values {:sql-query-id id
                        :name         (:sql-query/name query)}
       :submit         #(dispatch [:sql-console.api/edit-sql-query db-id id %])})]))


(defn close-drawer []
  (dispatch [:sql-editor/update {:sql-editor/sql-query-to-edit ""}]))


(defn drawers-render []
  (let [current-query @(re-posh/subscribe [:sql-editor/prop :sql-editor/sql-query-to-edit])
        valid-drawer? (and (not (nil? current-query))
                           (not (clojure.string/blank? current-query)))]
    [drawer {:open           valid-drawer?
             :on-open-change close-drawer
             :content (if valid-drawer?
                        [edit-query
                         {:id        current-query
                          :on-close  close-drawer}]
                        ;; This is here to keep the form from collapsing too fast when form disappears
                        [:div {:class "min-w-52"}])}]))


(defn go-to-data-view [data-view]
  (dispatch [:app.events/navigate :data-view-details {:data-view-id (:data-view/data-view-id data-view)}]))

(defn on-data-view-create [name code]
  (let [on-success (fn [data-view]
                     (close-create-data-view-modal)
                     (close-sql-console-modal)
                     (go-to-data-view data-view))]
    (dispatch [:data-views.api/create-data-view {:params {:name name :command code}
                                                 :on-success on-success}])))

(defn create-data-view-modal-render []
  (let [name      @(re-posh/subscribe [:sql-editor/prop :sql-editor/data-view-name])
        open?     @(re-posh/subscribe [:sql-editor/prop :sql-editor/create-data-view-modal-open?])

        tab-idx   (or @(re-posh/subscribe [:sql-editor/prop :sql-editor/current-tab]) 0)
        tdc       @(re-posh/subscribe [:sql-editor/prop :sql-editor/tab-data-container])
        tab-data  (nth tdc tab-idx {})

        code      (:code tab-data)
        on-create #(on-data-view-create name code)
        on-change (fn [e]
                    (dispatch [:sql-editor/update (->sql-editor {:data-view-name (js-interop/get-in e [:target :value])})]))]
    [dialog {:open           open?
             :title          "Save data view"
             :on-open-change close-create-data-view-modal
             :content

             [:div {:class "p-6 flex flex-col items-center gap-6"}
              [:div {:class "text-white bg-kleene-green p-4 shadow-xs rounded-full leading-zero"}
               [:> Database {:class "w-12! h-12!"}]]
              [typography/p
               "Please enter the name of the data view"]

              [input {:label      "Data view name"
                      :on-change  on-change
                      :value      name
                      :full-width true
                      :auto-focus true
                      :data-cy    "sql-editor-create-data-view-input"}]
              [dialog-actions {:class "w-full"}
               [button {:on-click   close-create-data-view-modal
                        :full-width true
                        :variant    :secondary
                        :data-cy    "sql-edit-create-data-view-cancel"}
                "Cancel"]
               [button {:on-click   on-create
                        :disabled   (or (empty? name) (empty? code))
                        :full-width true
                        :data-cy    "sql-edit-create-data-view-save"}
                "Save"]]]}]))


(defn main []
  (reagent/create-class
   {:display-name "home-screen"

    :constructor
    (fn []
      (dispatch [:app.events/navigate :sources])
      (dispatch [:sql-console-modal.events/toggle-modal true]))

    :render
    (fn [] [:div])}))
